export const SCOPE_RESOURCES = ['forms', 'messages', 'files', 'integrations', 'analytics'] as const;
export type ScopeResource = (typeof SCOPE_RESOURCES)[number];
export const SCOPE_LEVELS = ['none', 'read', 'write', 'delete'] as const;
export type ScopeLevel = (typeof SCOPE_LEVELS)[number];
export type ScopeMatrix = Record<ScopeResource, ScopeLevel>;

export const ALLOWED_LEVELS: Record<ScopeResource, readonly ScopeLevel[]> = {
  forms: SCOPE_LEVELS,
  messages: SCOPE_LEVELS,
  // Files are read and deleted, never changed.
  files: ['none', 'read', 'delete'],
  integrations: SCOPE_LEVELS,
  analytics: ['none', 'read'],
};

export const EMPTY_MATRIX: ScopeMatrix = {
  forms: 'none',
  messages: 'none',
  files: 'none',
  integrations: 'none',
  analytics: 'none',
};

/** The backend treats `write` as read and change, and `<r>:*` as read, change and delete. */
const PERMISSION: Record<Exclude<ScopeLevel, 'none'>, string> = {
  read: 'read',
  write: 'write',
  delete: '*',
};

const LEVEL_OF: Record<string, ScopeLevel> = { read: 'read', write: 'write', '*': 'delete' };

const rank = (level: ScopeLevel) => SCOPE_LEVELS.indexOf(level);

const isResource = (value: string): value is ScopeResource =>
  (SCOPE_RESOURCES as readonly string[]).includes(value);

const isAllowance = (item: string) => item.startsWith('allowance:');

/** The level an entry gives in the matrix, or null when the matrix cannot show it. */
function matrixEntry(item: string): [ScopeResource, ScopeLevel] | null {
  const [resource, permission] = item.split(':');
  if (!isResource(resource)) return null;
  const level = LEVEL_OF[permission];
  if (!level || !ALLOWED_LEVELS[resource].includes(level)) return null;
  return [resource, level];
}

export function matrixToScope(matrix: ScopeMatrix): string[] {
  return SCOPE_RESOURCES.flatMap((r) => {
    const level = matrix[r];
    return level === 'none' ? [] : [`${r}:${PERMISSION[level]}`];
  });
}

export function scopeToMatrix(scope: string[] | undefined | null): ScopeMatrix {
  const matrix = { ...EMPTY_MATRIX };
  const raise = (r: ScopeResource, level: ScopeLevel) => {
    if (rank(level) > rank(matrix[r])) matrix[r] = level;
  };
  for (const item of scope ?? []) {
    if (item === '*') {
      for (const r of SCOPE_RESOURCES) raise(r, ALLOWED_LEVELS[r][ALLOWED_LEVELS[r].length - 1]);
      continue;
    }
    const entry = matrixEntry(item);
    if (entry) raise(...entry);
  }
  return matrix;
}

/** True for `*` or any entry the matrix cannot express, such as `analytics:*`. */
export function isFullScope(scope: string[] | undefined | null): boolean {
  return (scope ?? []).some((item) => item === '*' || (!isAllowance(item) && !matrixEntry(item)));
}
